/**
 * LP の計測（GA4 イベント）の配線。検索 LP と配信 LP の共通部品から呼ぶ。
 *
 * 2 つの LP は `trackEvent` が強制注入する `page_location`（論理パス `/lp` と `/lp/delivery`）で分かれる。
 * 論理パスは `LpRoute` / `LpDeliveryRoute` の `useLogicalPageView` が決める。クエリ `lp_id` は使わない。
 *
 * PII 方針（lib/analytics.ts の 4）: パラメータは列挙値と数値だけ。自由入力（会社名・氏名・メール・電話）は
 * 渡さない。チーム人数も表示ラベルではなく `LpTeamSize` のキーに変換してから送る。
 * 測定 ID の無い環境では `trackEvent` 側で no-op になる。
 */
import {
  type LpCtaDest,
  type LpCtaId,
  type LpFormErrorReason,
  type LpFormField,
  type LpSectionId,
  type LpTeamSize,
  trackEvent,
} from "lib/analytics";
import { useCallback, useEffect, useRef } from "react";

/** 計測する CTA の遷移先。これ以外（外部 URL など）の CTA は送らない。 */
const CTA_DESTS: readonly LpCtaDest[] = [
  "#cta",
  "#pricing",
  "/signup",
  "/login",
];

/** 料金プラン名 → CTA の識別子。 */
const PRICING_CTA_IDS: Readonly<Record<string, LpCtaId>> = {
  送る: "pricing_send",
  Free: "pricing_free",
  Starter: "pricing_starter",
  Standard: "pricing_standard",
  Professional: "pricing_professional",
  Enterprise: "pricing_enterprise",
} as const;

/** 営業担当者数の表示ラベル → 計測用のキー。 */
const TEAM_SIZES: Readonly<Record<string, LpTeamSize>> = {
  "1-5名": "1-5",
  "6-15名": "6-15",
  "16-50名": "16-50",
  "51名以上": "51-plus",
} as const;

/**
 * 対応表から自分のキーだけを引く（`toString` などプロトタイプのキーを拾わない）。
 * @param {object} table 対応表
 * @param {string} key 引くキー
 * @return {V | undefined} 値。無ければ undefined
 */
function lookup<V>(
  table: Readonly<Record<string, V>>,
  key: string,
): V | undefined {
  return Object.keys(table).includes(key) ? table[key] : undefined;
}

/**
 * CTA の href を計測用の遷移先に変換する。
 * @param {string} href CTA の href
 * @return {LpCtaDest | null} 計測する遷移先。計測しない遷移先なら null
 */
export function toLpCtaDest(href: string): LpCtaDest | null {
  return CTA_DESTS.find(dest => dest === href) ?? null;
}

/**
 * CTA のクリックを送る（`lp_cta_click`）。遷移先が計測対象外なら送らない。
 * @param {LpCtaId} ctaId CTA の識別子
 * @param {string} href CTA の href
 * @return {void}
 */
export function trackLpCta(ctaId: LpCtaId, href: string): void {
  const dest = toLpCtaDest(href);
  if (dest === null) {
    return;
  }
  trackEvent("lp_cta_click", { cta_id: ctaId, cta_dest: dest });
}

/**
 * 料金プラン名を CTA の識別子に変換する。
 * @param {string} planName 料金プラン名
 * @return {LpCtaId | null} CTA の識別子。対応が無ければ null
 */
export function pricingCtaId(planName: string): LpCtaId | null {
  return lookup(PRICING_CTA_IDS, planName) ?? null;
}

/**
 * セクションが初めて見えたとき、そのマウント中 1 回だけ `lp_section_view` を送る。
 *
 * `isVisible` が false → true → false → true と変わっても 2 回目は送らない。
 * 別のマウント（ページの再訪）ではまた 1 回送る。
 * @param {LpSectionId} sectionId セクションの識別子
 * @param {boolean} isVisible セクションが見えているか（`useFadeIn` の isVisible）
 * @return {void}
 */
export function useLpSectionView(
  sectionId: LpSectionId,
  isVisible: boolean,
): void {
  const sentRef = useRef(false);

  useEffect(() => {
    if (!isVisible || sentRef.current) {
      return;
    }
    sentRef.current = true;
    trackEvent("lp_section_view", { section_id: sectionId });
  }, [sectionId, isVisible]);
}

/**
 * FAQ を開いたことを送る（`lp_faq_open`）。閉じるときは呼ばない。
 * @param {number} faqIndex 開いた FAQ の添字
 * @return {void}
 */
export function trackLpFaqOpen(faqIndex: number): void {
  trackEvent("lp_faq_open", { faq_index: faqIndex });
}

/**
 * フォームの最初の入力を送る関数を返す。返した関数はそのマウント中の最初の 1 回だけ `lp_form_start` を送る。
 *
 * 返す関数は再レンダリングをまたいで同じ参照（`useCallback`）。
 * @return {Function} 入力のあったフィールド名を受け取る関数
 */
export function useLpFormStart(): (field: LpFormField) => void {
  const sentRef = useRef(false);

  return useCallback((field: LpFormField) => {
    if (sentRef.current) {
      return;
    }
    sentRef.current = true;
    trackEvent("lp_form_start", { field });
  }, []);
}

/**
 * 営業担当者数の表示ラベルを計測用のキーに変換する。
 * @param {string} label 表示ラベル（例 "1-5名"）
 * @return {LpTeamSize} 計測用のキー。該当が無ければ "unselected"
 */
export function toLpTeamSize(label: string): LpTeamSize {
  return lookup(TEAM_SIZES, label) ?? "unselected";
}

/**
 * 問い合わせの送信成功を送る（`generate_lead`）。表示ラベルはキーに変換してから送る。
 * @param {string} teamSizeLabel 営業担当者数の表示ラベル
 * @return {void}
 */
export function trackLpLead(teamSizeLabel: string): void {
  trackEvent("generate_lead", { team_size: toLpTeamSize(teamSizeLabel) });
}

/**
 * 送信失敗の HTTP ステータスを理由に変換する。
 * @param {number | null} status HTTP ステータス。fetch が投げた場合は null
 * @return {LpFormErrorReason} 失敗の理由
 */
export function lpFormErrorReason(status: number | null): LpFormErrorReason {
  if (status === null) return "network";
  if (status === 429) return "rate_limited";
  if (status === 400 || status === 422) return "validation";
  return "http_error";
}

/**
 * 問い合わせの送信失敗を送る（`lp_form_error`）。
 * @param {LpFormErrorReason} reason 失敗の理由
 * @return {void}
 */
export function trackLpFormError(reason: LpFormErrorReason): void {
  trackEvent("lp_form_error", { reason });
}
